import { Controller } from "@hotwired/stimulus";

import { prefersReducedMotion } from "~/lib/viewport";
import { withCommas } from "~/lib/web/counter";
import {
  clamp01,
  easeBackOut,
  easeInOutCubic,
  easeOutCubic,
} from "~/lib/web/ease";
import { whileInView } from "~/lib/web/loop";

const PILL_HEIGHT = 56;
const PILL_TOP = 168;

/**
 * Space reserved to the right of the input's text for the send button, so the
 * question cannot overlap it. The matching inset on the left is the
 * stylesheet's own `left`, read in measure() rather than stated twice.
 */
const PILL_PAD_RIGHT = 46;

/** Space after the chip's label. Its left inset is the icon zone, also read. */
const CHIP_PAD_RIGHT = 16;

const PLOT_HEIGHT = 150;
const BAR_STAGGER_MS = 80;
const ROW_STAGGER_MS = 200;

/** Space under the last row once the transcript has scrolled to its end. */
const SCROLL_GAP = 24;

/** How much of the card has to be in view before the typing starts. */
const IN_VIEW_THRESHOLD = 0.45;

/**
 * The loop, as [name, duration, lead] where `lead` is how far a beat starts
 * before the previous one ends, so overlaps are declared rather than emergent.
 * That ordering is structural: the answer must not begin before the tool call
 * it reports has resolved.
 */
const BEATS: [string, number, number?][] = [
  ["pill", 93], // held stillness before the first character
  /* About 37 characters a second over the 52 of the question: slow enough to
     look typed and fast enough to read. The time is taken out of hold2, so the
     total the scroll bounds derive from is unchanged. */
  ["type", 1400],
  ["typed", 155], // a pause on the finished question, before sending
  ["send", 243], // the largest single move in the asset
  ["tool1", 557],
  ["resolve1", 243], // the check, and it names us
  ["answer1", 2000],
  ["chart", 1000], // bars stagger inside this
  /* The stream may outrun reading as long as a hold follows it. The autoscroll
     also may not start before this ends, so the first exchange is read without
     the transcript moving. */
  ["hold1", 1400],
  ["q2", 600],
  ["tool2", 557],
  ["resolve2", 243],
  ["answer2", 1600],
  ["rows2", 1000], // rows stagger; the scroll-in lands with them
  ["close", 620, 200], // the pause held after the last answer
  ["hold2", 1989], // the donor for the typing beat above
  ["clear", 500], // the conversation fades and the card collapses back
];

interface Stream {
  segments: HTMLElement[];
  texts: string[];
  stops: number[];
}

interface Parts {
  shift: HTMLElement;
  pill: HTMLElement;
  typed: HTMLElement;
  convo: HTMLElement;
  questions: HTMLElement[];
  chips: HTMLElement[];
  answers: HTMLElement[];
  chart: HTMLElement | null;
  rows: HTMLElement[];
  pillLine: HTMLElement;
  pillPlus: HTMLElement;
  pillSend: HTMLElement;
  bars: HTMLElement[];
  values: HTMLElement[];
  labels: HTMLElement[];
  footv: HTMLElement | null;
  asked: HTMLElement[];
  questionText: string[];
  streams: Stream[];
  chipCalling: number[];
  chipDone: number[];
  chipHeight: number;
  pillRest: number;
  pillPadY: number;
  handoverX: number;
  handoverY: number;
  questionTop: number;
  distance: number;
}

const timeline = (): {
  windows: Map<string, [number, number]>;
  total: number;
} => {
  const windows = new Map<string, [number, number]>();
  let cursor = 0;
  for (const [name, duration, lead] of BEATS) {
    const start = Math.max(0, cursor - (lead ?? 0));
    cursor = start + duration;
    windows.set(name, [start, cursor]);
  }
  return { windows, total: cursor };
};

/**
 * Whole words, never a partial one. Revealing character by character changes the
 * last word's width on every frame, which re-wraps the line under it.
 */
const stream = (block: Stream, progress: number): void => {
  const count = Math.round(progress * block.stops.length);
  const shown =
    count <= 0
      ? 0
      : (block.stops[Math.min(count, block.stops.length) - 1] ?? 0);
  let consumed = 0;
  block.texts.forEach((text, index) => {
    const take = Math.max(0, Math.min(text.length, shown - consumed));
    const segment = block.segments[index];
    if (segment !== undefined) segment.textContent = text.slice(0, take);
    consumed += text.length;
  });
};

export default class WebMcpController extends Controller<HTMLElement> {
  static override targets = ["fade"];
  declare readonly fadeTarget: HTMLElement;
  declare readonly hasFadeTarget: boolean;

  private readonly timeline = timeline();
  private pristine: Node | null = null;
  private parts: Parts | null = null;
  private scroll: Animation | null = null;
  private running = false;
  private started = 0;
  private frame = 0;
  private loopId = 0;
  private lastWidth = 0;
  private stop: (() => void) | null = null;
  private resize: ResizeObserver | null = null;
  private listeners = new AbortController();

  override connect(): void {
    if (!this.hasFadeTarget || prefersReducedMotion()) return;
    const first = this.fadeTarget.firstElementChild;
    if (first === null) return;

    /* Every entrance is a CSS transition, and a transition in flight cannot be
       rewound in the same tick as the dozen other writes an in-place reset
       needs: any disagreement paints a frame of the old content at the new
       content's opacity. So the loop restarts by rebuilding the subtree from
       this clone, taken before anything below modifies it. */
    this.pristine = first.cloneNode(true);

    // The stylesheet renders the finished conversation until this is set.
    this.element.classList.add("is-animating");

    this.stop = whileInView(
      this.element,
      () => this.start(),
      () => this.pause(),
      IN_VIEW_THRESHOLD,
    );

    /* The display face changes where the answers wrap, and so the scroll
       distance. Measured before that font loads it can be 17px out. */
    void document.fonts.ready.then(() => {
      if (this.parts !== null) this.measure();
    });

    /* Width only, and this is load bearing: the transcript's height changes on
       almost every frame while an answer streams, and rebuilding the scroll
       animation on each of those frames is visible as a stutter. */
    this.resize = new ResizeObserver(() => {
      const parts = this.parts;
      if (parts === null) return;
      const width = parts.convo.offsetWidth;
      if (width === this.lastWidth) return;
      this.lastWidth = width;
      this.measure();
    });
    this.resize.observe(this.element);
  }

  override disconnect(): void {
    this.element.classList.remove("is-animating");
    this.stop?.();
    this.stop = null;
    this.pause();
    this.resize?.disconnect();
    this.resize = null;
    this.scroll?.cancel();
    this.scroll = null;
    this.parts = null;
    this.listeners.abort();
    this.listeners = new AbortController();
  }

  /**
   * Re-measured on every entrance rather than once at connect: the widths depend
   * on the column and on the face the question is set in, and at connect the web
   * font may not have arrived yet.
   */
  private start(): void {
    this.running = true;
    this.parts ??= this.attach();
    if (this.parts === null) return;
    this.measure();
    this.fadeTarget.style.opacity = "1";
    this.started = 0;
    this.schedule();
  }

  private pause(): void {
    this.running = false;
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.loopId++;
  }

  /**
   * Starts the frame loop, invalidating any loop already running. Without the
   * id, two loops can assign `frame` at once, and then pause() and disconnect()
   * cancel one and leave the other running: if the element leaves view while a
   * finished timeline's fade is still in flight, start() sets opacity back to 1
   * and reverses that fade, so its transitionend still arrives and swap
   * schedules a second loop over the one start() began.
   */
  private schedule(): void {
    const loopId = ++this.loopId;
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame((now) => this.tick(now, loopId));
  }

  private window(key: string): [number, number] {
    return this.timeline.windows.get(key) ?? [0, 1];
  }

  private progress(t: number, key: string): number {
    const [from, to] = this.window(key);
    return clamp01((t - from) / (to - from));
  }

  /**
   * Queried fresh after every rebuild: none of these nodes survive a restart.
   * Returns null rather than throwing if the markup is not what it expects.
   */
  private attach(): Parts | null {
    const shift = this.fadeTarget.firstElementChild;
    if (!(shift instanceof HTMLElement)) return null;
    const pick = <T extends HTMLElement>(selector: string): T | null =>
      shift.querySelector<T>(selector);
    const all = <T extends HTMLElement>(selector: string): T[] => [
      ...shift.querySelectorAll<T>(selector),
    ];

    const pill = pick("[data-mcp-pill]");
    const typed = pick("[data-mcp-typed]");
    const convo = pick("[data-mcp-convo]");
    const pillLine = pick(".web-chat__pill-text");
    const pillPlus = pick(".web-chat__plus");
    const pillSend = pick(".web-chat__send");
    const questions = all("[data-mcp-q]");
    if (
      pill === null ||
      typed === null ||
      convo === null ||
      pillLine === null ||
      pillPlus === null ||
      pillSend === null ||
      !questions.length
    ) {
      return null;
    }

    const asked = questions.flatMap((question) => {
      const node = question.querySelector<HTMLElement>("[data-mcp-question]");
      return node === null ? [] : [node];
    });

    /* Each answer's segments are cached and then cleared: the markup contains
       the finished conversation for a reader without the script, and this is
       where it is emptied so it can be streamed. The stops are the word
       boundaries across the whole answer, bold spans included, so a reveal
       always lands between words. */
    const streams = all<HTMLElement>("[data-mcp-ans]").map((answer) => {
      const segments = [
        ...answer.querySelectorAll<HTMLElement>("[data-mcp-seg]"),
      ];
      const texts = segments.map((segment) => segment.textContent ?? "");
      for (const segment of segments) segment.textContent = "";
      const joined = texts.join("");
      const stops: number[] = [];
      for (const match of joined.matchAll(/\S+\s*/g)) {
        stops.push(match.index + match[0].length);
      }
      return { segments, texts, stops };
    });

    // Only the first question types. The second appears whole, so its text
    // stays in the node the markup rendered it in.
    typed.textContent = "";

    return {
      shift,
      pill,
      typed,
      convo,
      questions,
      chips: all("[data-mcp-chip]"),
      answers: all("[data-mcp-ans]"),
      chart: pick("[data-mcp-chart]"),
      rows: all("[data-mcp-row]"),
      pillLine,
      pillPlus,
      pillSend,
      bars: all("[data-mcp-bar]"),
      values: all("[data-mcp-value]"),
      labels: all(".web-chat__bar-label"),
      footv: pick(".web-chat__footer-value"),
      asked,
      questionText: asked.map((node) => node.textContent ?? ""),
      streams,
      chipCalling: [],
      chipDone: [],
      chipHeight: 34,
      pillRest: 0,
      pillPadY: 0,
      handoverX: 0,
      handoverY: 0,
      questionTop: 0,
      distance: 0,
    };
  }

  /**
   * Measured with both answers printed in full. The transcript is at its tallest
   * only once the last word has arrived, and measuring whatever is on screen
   * mid-stream gives a distance that is short by several lines.
   */
  private scrollDistance(parts: Parts): number {
    const last = parts.rows.at(-1);
    if (last === undefined) return 0;
    this.element.classList.add("is-measuring");
    const held: [HTMLElement, string][] = [];
    for (const block of parts.streams) {
      block.segments.forEach((segment, index) => {
        held.push([segment, segment.textContent ?? ""]);
        segment.textContent = block.texts[index] ?? "";
      });
    }
    const bottom =
      last.getBoundingClientRect().bottom -
      parts.shift.getBoundingClientRect().top;
    for (const [segment, text] of held) segment.textContent = text;
    this.element.classList.remove("is-measuring");
    return Math.max(
      0,
      Math.round(bottom - this.element.clientHeight + SCROLL_GAP),
    );
  }

  /**
   * One animation for the whole pass, scrubbed rather than played: its progress
   * is set from the same `t` every other property reads, so it cannot drift from
   * the beats its offsets were derived from, and a rebuild cannot leave it
   * running against a node that no longer exists.
   */
  private buildScroll(parts: Parts): void {
    this.scroll?.cancel();
    this.scroll = null;
    if (!parts.distance) return;

    const from = this.window("hold1")[1];
    const span = this.timeline.total - from;
    const scrollIn = (this.window("rows2")[1] - from) / span;
    const scrollHeld = (this.window("hold2")[1] - from) / span;
    this.scroll = parts.shift.animate(
      [
        { transform: "translateY(0)", offset: 0 },
        { transform: `translateY(${-parts.distance}px)`, offset: scrollIn },
        { transform: `translateY(${-parts.distance}px)`, offset: scrollHeld },
        { transform: "translateY(0)", offset: 1 },
      ],
      { duration: span, easing: "linear", fill: "both" },
    );
    this.scroll.pause();
  }

  /**
   * Sizes derived from the copy rather than chosen, because both elements they
   * size contain text that is absolutely positioned and so contributes no width
   * of its own. Left to `width: auto` the chip collapses onto its own padding
   * and shows about one character of its label.
   */
  private measure(): void {
    const parts = this.parts;
    if (parts === null) return;

    const inset = getComputedStyle(parts.pillLine);
    const padLeft = parseFloat(inset.left);
    parts.pillPadY = parseFloat(inset.top);

    // With the question fully typed: render() rewrites this every frame from
    // the timeline, so measuring what is on screen measures a partial string.
    const held = parts.typed.textContent;
    const question = parts.questionText[0] ?? "";
    parts.typed.textContent = question;
    parts.pillLine.style.width = "";
    parts.pillLine.style.whiteSpace = "nowrap";
    const natural = parts.pillLine.getBoundingClientRect().width;
    parts.pillLine.style.whiteSpace = "";

    // The question's own width, clamped to what is left of the column once both
    // control zones are subtracted. The slack keeps a last word from wrapping
    // onto a line of its own.
    const room = Math.max(
      140,
      parts.convo.offsetWidth - padLeft - PILL_PAD_RIGHT,
    );
    const line = Math.min(room, Math.ceil(natural) + 10);
    parts.pillLine.style.width = `${line}px`;
    // The bubble is given the same width, so the question wraps identically in
    // the input and in the bubble it moves to; a re-wrap in between would read
    // as two different questions.
    const firstAsked = parts.asked[0];
    if (firstAsked !== undefined) firstAsked.style.width = `${line}px`;
    parts.pillRest = padLeft + line + PILL_PAD_RIGHT;
    parts.typed.textContent = held;

    /* Against the shift rather than the panel: measured against the panel, a
       re-measure taken while the autoscroll is applied reads the bubble 200px
       above its actual position, and the comparison that uses it never passes. */
    const firstQuestion = parts.questions[0];
    if (firstQuestion !== undefined) {
      parts.questionTop =
        firstQuestion.getBoundingClientRect().top -
        parts.shift.getBoundingClientRect().top;
    }

    this.measureHandover(parts);
    this.measureChips(parts);
    this.measureBars(parts);

    parts.distance = this.scrollDistance(parts);
    this.buildScroll(parts);
  }

  /**
   * The vertical distance the copy still has to move once the box has finished
   * expanding. The copy is inside the pill, so the pill's own rise accounts for
   * most of the distance and this is the remainder.
   *
   * The transform is cleared first because this also runs on resize and on
   * fonts.ready: a move already in progress is a transform on the copy, which
   * getBoundingClientRect includes, and the remaining distance would measure as
   * zero.
   */
  private measureHandover(parts: Parts): void {
    const asked = parts.asked[0];
    if (asked === undefined) return;
    const travelled = parts.pillLine.style.transform;
    parts.pillLine.style.transform = "none";
    const pillBox = parts.pill.getBoundingClientRect();
    const lineBox = parts.pillLine.getBoundingClientRect();
    const askedBox = asked.getBoundingClientRect();
    const shiftBox = parts.shift.getBoundingClientRect();
    parts.handoverX = askedBox.left - lineBox.left;
    parts.handoverY = askedBox.top - shiftBox.top - (lineBox.top - pillBox.top);
    parts.pillLine.style.transform = travelled;
  }

  /**
   * Chip widths here rather than in render: reading a width while also writing
   * one in the same frame forces a synchronous layout, sixty times a second for
   * numbers that never change. The collapsed width comes off
   * the chip's own height, so the circle is a circle by construction.
   */
  private measureChips(parts: Parts): void {
    parts.chips.forEach((holder, index) => {
      const chip = holder.querySelector<HTMLElement>(".web-chat__chip");
      const call = chip?.querySelector<HTMLElement>(".web-chat__label--call");
      const done = chip?.querySelector<HTMLElement>(".web-chat__label--done");
      if (chip === null || chip === undefined || !call || !done) return;
      const pad = parseFloat(getComputedStyle(call).left) + CHIP_PAD_RIGHT;
      parts.chipHeight = chip.offsetHeight || parts.chipHeight;
      parts.chipCalling[index] = call.offsetWidth + pad;
      parts.chipDone[index] = done.offsetWidth + pad;
    });
  }

  /**
   * Each bar's full height, once. The growth is a scaleY from the bottom, so the
   * plot is laid out here and never again; animating the height instead would
   * relayout the whole card on every frame of the stagger.
   */
  private measureBars(parts: Parts): void {
    parts.bars.forEach((bar, index) => {
      const ratio = parseFloat(bar.getAttribute("data-mcp-bar") ?? "0");
      bar.style.height = `${ratio * PLOT_HEIGHT}px`;
      const value = parts.values[index];
      if (value !== undefined) {
        value.style.bottom = `${ratio * PLOT_HEIGHT + 6}px`;
      }
    });
  }

  private render(t: number): void {
    const parts = this.parts;
    if (parts === null) return;
    // Past this the beat table is reversing, so the entrances are released and
    // their own transitions play out.
    const live = t < this.window("clear")[0];

    this.renderComposer(parts, t, live);
    this.renderTurns(parts, t, live);
    this.renderChart(parts, t, live);
    this.renderRows(parts, t, live);

    if (this.scroll !== null) {
      const from = this.window("hold1")[1];
      const span = this.timeline.total - from;
      this.scroll.currentTime = clamp01((t - from) / span) * span;
    }
  }

  /** The pill types, holds, then expands into the box holding the conversation. */
  private renderComposer(parts: Parts, t: number, live: boolean): void {
    const question = parts.questionText[0] ?? "";
    const typing = this.progress(t, "type");
    parts.typed.textContent = question.slice(
      0,
      Math.round(typing * question.length),
    );
    this.element.classList.toggle(
      "is-typing",
      t < this.window("typed")[1] && t >= this.window("pill")[0],
    );

    /* One value sets width, height, top and radius together, and the clear beat
       subtracts from it so the card is a pill again before the subtree is
       rebuilt. Eased in and out, since a linear expansion reads as a scrollbar
       being dragged rather than as one object moving. */
    const sent = this.progress(t, "send");
    const opened = easeInOutCubic(clamp01(sent - this.progress(t, "clear")));
    const cardTop = PILL_TOP * (1 - opened);
    const fullWidth = parts.convo.offsetWidth;
    const fullHeight = parts.convo.offsetHeight;
    // Read before anything is written. The resting height follows the question's
    // own line count, so a column too narrow to hold it on one line grows the
    // input by a whole line rather than hiding the second one.
    const restHeight = Math.max(
      PILL_HEIGHT,
      parts.pillPadY * 2 + parts.pillLine.offsetHeight,
    );
    const restWidth = Math.min(fullWidth, parts.pillRest || fullWidth);
    parts.pill.style.width = `${restWidth + (fullWidth - restWidth) * opened}px`;
    parts.pill.style.height = `${restHeight + (fullHeight - restHeight) * opened}px`;
    parts.pill.style.top = `${cardTop}px`;
    parts.pill.style.borderRadius = `${28 - 12 * opened}px`;
    /* The stylesheet's base is zero, so a page without this script does not
       paint a stray composer over the static transcript. It is raised here and
       never lowered: the box is never hidden, and the wrapper's fade is what
       removes it at the end of the loop. */
    parts.pill.style.opacity = "1";

    /* The copy moves into the bubble instead of fading out where it was typed.
       Set from the same `opened` as the box, so the two compose into one straight
       line: the pill's growth accounts for part of the distance and this
       accounts for the rest. */
    parts.pillLine.style.transform = `translate(${parts.handoverX * opened}px,${parts.handoverY * opened}px)`;
    /* Held until the last third of the move, then faded out. By then the copy is
       over the bubble at the same size and tracking, and the bubble is fading up
       underneath it, so the two never read as separate runs of words. */
    parts.pillLine.style.opacity = String(1 - clamp01((sent - 0.67) / 0.33));
    const controls = String(1 - easeOutCubic(clamp01(sent * 2)));
    parts.pillPlus.style.opacity = controls;
    parts.pillSend.style.opacity = controls;

    const firstQuestion = parts.questions[0];
    if (firstQuestion !== undefined) {
      // Gated on the card's own top edge, not on the send's progress: the edge
      // descends as the card opens, and at a fixed fraction of the send the
      // bubble was revealed above the card it belongs to.
      firstQuestion.classList.toggle(
        "is-in",
        live && parts.questionTop - cardTop > 0,
      );
    }
    const secondQuestion = parts.questions[1];
    if (secondQuestion !== undefined) {
      secondQuestion.classList.toggle(
        "is-in",
        live && t >= this.window("q2")[0],
      );
    }
  }

  private renderTurns(parts: Parts, t: number, live: boolean): void {
    parts.questions.forEach((_question, turn) => {
      const suffix = turn === 0 ? "1" : "2";
      const tool = this.progress(t, `tool${suffix}`);
      const resolved = this.progress(t, `resolve${suffix}`);
      const shown = live && tool > 0;

      const holder = parts.chips[turn];
      const chip = holder?.querySelector<HTMLElement>(".web-chat__chip");
      holder?.classList.toggle("is-in", shown);
      if (chip) {
        chip.classList.toggle("is-calling", tool > 0 && resolved <= 0);
        chip.classList.toggle("is-done", resolved > 0);
        // Opens out of the bare circle to hold its label, then settles onto the
        // shorter one as it resolves, so the chip reads as one object gaining a
        // caption rather than an icon being swapped for a pill.
        const calling = parts.chipCalling[turn] ?? parts.chipHeight;
        const done = parts.chipDone[turn] ?? calling;
        chip.style.width = `${
          resolved > 0
            ? calling + (done - calling) * easeOutCubic(resolved)
            : parts.chipHeight +
              (calling - parts.chipHeight) * easeOutCubic(tool)
        }px`;
        const check = chip.querySelector<HTMLElement>(".web-chat__icon--check");
        if (check) {
          check.style.transform = `scale(${resolved > 0 ? easeBackOut(resolved) : 0})`;
        }
      }

      const answered = this.progress(t, `answer${suffix}`);
      const answer = parts.answers[turn];
      if (answer === undefined) return;
      answer.classList.toggle("is-in", shown);
      const think = answer.querySelector<HTMLElement>(".web-chat__thinking");
      if (think)
        think.style.opacity = String(tool > 0 && answered === 0 ? 1 : 0);
      const block = parts.streams[turn];
      if (block !== undefined) stream(block, answered);
    });
  }

  /** The chart is drawn only after the first answer has been streamed. */
  private renderChart(parts: Parts, t: number, live: boolean): void {
    const drawn = this.progress(t, "chart");
    parts.chart?.classList.toggle("is-in", live && drawn > 0);
    const [from, to] = this.window("chart");
    const chartMs = to - from;
    const barSpan = chartMs - BAR_STAGGER_MS * (parts.bars.length - 1);

    parts.bars.forEach((bar, index) => {
      const p = clamp01((drawn * chartMs - index * BAR_STAGGER_MS) / barSpan);
      const grown = p > 0 ? easeBackOut(p) : 0;
      // The bar is allowed past its target and back; the figure under it is
      // not, or it would count past the number it reports.
      const settled = Math.min(1, grown);
      bar.style.transform = `scaleY(${grown})`;
      const ratio = parseFloat(bar.getAttribute("data-mcp-bar") ?? "0");
      const value = parts.values[index];
      if (value !== undefined) {
        // Tracks the bar's current top with a transform. Its `bottom` is the
        // bar's full height, set once in measure(), so nothing is re-laid out.
        value.style.transform = `translateY(${(1 - grown) * ratio * PLOT_HEIGHT}px)`;
        const full = Number(value.getAttribute("data-mcp-value") ?? 0);
        value.textContent = withCommas(Math.round(full * settled));
        value.style.opacity = String(settled);
      }
      const label = parts.labels[index];
      if (label !== undefined) label.style.opacity = String(settled);
    });

    if (parts.footv) {
      parts.footv.style.opacity = String(
        easeOutCubic(clamp01(drawn * 1.2 - 0.3)),
      );
    }
  }

  private renderRows(parts: Parts, t: number, live: boolean): void {
    const [from, to] = this.window("rows2");
    const elapsed = this.progress(t, "rows2") * (to - from);
    parts.rows.forEach((row, index) => {
      row.classList.toggle(
        "is-in",
        live && elapsed - index * ROW_STAGGER_MS > 0,
      );
    });
  }

  private tick(now: number, loopId: number): void {
    if (loopId !== this.loopId) return;
    if (!this.started) this.started = now;
    const t = now - this.started;
    if (t >= this.timeline.total) {
      this.frame = 0;
      // The rebuild waits for the fade's transitionend, not for this frame.
      this.fadeTarget.style.opacity = "0";
      this.fadeTarget.addEventListener("transitionend", this.swap, {
        signal: this.listeners.signal,
      });
      return;
    }
    this.render(t);
    this.frame = requestAnimationFrame((next) => this.tick(next, loopId));
  }

  /**
   * Filtered on the element and the property, and that is the whole reason the
   * restart works: every entrance inside this subtree transitions opacity too,
   * and those events bubble to here. A bare listener fires on the first of them
   * and swaps the DOM while the old generation is still fully visible.
   */
  private readonly swap = (event: Event): void => {
    if (!(event instanceof TransitionEvent)) return;
    if (event.target !== this.fadeTarget || event.propertyName !== "opacity") {
      return;
    }
    this.fadeTarget.removeEventListener("transitionend", this.swap);
    this.scroll?.cancel();
    this.scroll = null;
    if (this.pristine === null) return;
    this.fadeTarget.replaceChildren(this.pristine.cloneNode(true));
    this.parts = this.attach();
    if (this.parts === null) return;
    this.measure();
    this.render(0);
    /* Two frames, because one is not enough: the class and style writes above
       and the paint that resolves them land in the same frame, so revealing on
       the next one shows the fresh generation mid-layout. */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.running) return;
        this.fadeTarget.style.opacity = "1";
        this.started = 0;
        this.schedule();
      });
    });
  };
}
